import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import KanbanCard from './KanbanCard';
import TaskDetailsModal from './TaskDetailsModal';
import NovaTarefaModal from './NovaTarefaModal';
import { useKanban } from '../../contexts/KanbanContext';
import { Tarefa, Quadro, StatusTarefa } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useNavegacao } from '../../contexts/NavegacaoContext';
import { colunasRecebidas, colunasEnviadas } from '../../data/mockData';
import tarefaService from '../../services/tarefaService';
import { toast } from 'react-toastify';
import { Button, IconButton, Tooltip } from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
// HelpOutlineIcon removido pois não é mais utilizado
// RefreshIcon removido pois não é mais utilizado
import { useNavigate, useLocation } from 'react-router-dom';
import { mapearComentarios } from '../tarefas/ComentarioMapper';
// KanbanTutorialContext removido conforme solicitado

interface KanbanBoardProps {
  tipo: 'recebidas' | 'enviadas';
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ tipo }) => {
  const { quadro, moverTarefa, atualizarTarefa, recarregarTarefas, atualizacoesTempoReal } = useKanban();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.tipo === 'admin';
  const { ehLiderDepartamento, usuariosDepartamento, departamentoAtual } = useDepartamento();
  const { rotaAtual } = useNavegacao();
  // Tutorial removido conforme solicitado
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [novaTarefaModalAberto, setNovaTarefaModalAberto] = useState(false);
  const [colunaInicialNovaTarefa, setColunaInicialNovaTarefa] = useState('Solicitado');
  const [tarefaParaDuplicar, setTarefaParaDuplicar] = useState<Tarefa | null>(null);
  const [quadroFiltrado, setQuadroFiltrado] = useState<Quadro>({ id: '', titulo: '', colunas: [] });
  // Para líderes, sempre mostrar tarefas do departamento
  // Para usuários comuns, nunca mostrar tarefas do departamento (removido o botão de alternar)
  const [mostrarTarefasDepartamento] = useState<boolean>(
    ehLiderDepartamento && departamentoAtual !== null
  );
  // Estado atualizando removido pois não é mais necessário
  const navigate = useNavigate();
  const location = useLocation();
  
  // Referência para o container de scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Estado para controlar o arrasto para scroll
  const [isDraggingScroll, setIsDraggingScroll] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  // Função para lidar com o fim do arrasto de uma tarefa
  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // Se não houver destino ou se o destino for o mesmo que a origem, não fazer nada
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    // Encontrar a coluna de origem e destino
    const colunaOrigem = quadroFiltrado.colunas.find(col => col.id === source.droppableId);
    const colunaDestino = quadroFiltrado.colunas.find(col => col.id === destination.droppableId);
    
    if (!colunaOrigem || !colunaDestino) return;
    
    // Encontrar a tarefa que está sendo arrastada
    const tarefa = colunaOrigem.tarefas.find(t => t.id === draggableId);
    if (!tarefa) return;
    
    try {
      // Atualizar o status da tarefa na API
      const novoStatus = colunaDestino.titulo as StatusTarefa;
      await tarefaService.atualizarStatusTarefa(draggableId, novoStatus);
      
      // Atualizar a tarefa no contexto com o novo status
      const tarefaAtualizada = { ...tarefa, status: novoStatus };
      
      // Usar apenas atualizarTarefa que já faz a atualização local e na API
      // Isso evita o conflito com moverTarefa
      await atualizarTarefa(tarefaAtualizada);
      
      toast.success(`Tarefa movida para ${novoStatus}`);
    } catch (error: any) {
      console.error('Erro ao atualizar status da tarefa:', error);
      const mensagemBackend = error?.response?.data?.message;
      toast.error(mensagemBackend || 'Erro ao mover tarefa. Tente novamente.');

      // Recarregar tarefas para garantir consistência
      await recarregarTarefas();
    }
  };
  
  /**
   * Função para atualizar os dados do quadro kanban
   * 
   * Esta função é responsável por recarregar as tarefas do quadro kanban com base no tipo
   * (recebidas/enviadas) e na condição do usuário (líder ou não).
   * 
   * Comportamentos especiais:
   * 1. Para líderes de departamento: sempre carrega todas as tarefas do departamento
   *    quando há um departamento atual definido, independentemente do toggle de visualização.
   * 2. Para usuários comuns: carrega apenas suas próprias tarefas (onde são responsáveis
   *    no quadro "recebidas" ou criadores no quadro "enviadas").
   * 
   * @param silencioso Se true, não exibe notificações de carregamento
   */
  const atualizarDados = useCallback(async (silencioso = true) => {
    try {
      console.log('Iniciando atualização de dados do Kanban');
      console.log(`[DEBUG] atualizarDados - Parâmetros: ehLiderDepartamento=${ehLiderDepartamento}, mostrarTarefasDepartamento=${mostrarTarefasDepartamento}, tipo=${tipo}`);
      
      // Calcular o valor do parâmetro tarefasDepartamento
      // IMPORTANTE: Para líderes de departamento, sempre mostramos as tarefas do departamento
      // independentemente do toggle, desde que haja um departamento atual definido.
      // Isso garante que líderes sempre vejam todas as tarefas do seu departamento automaticamente.
      const tarefasDepartamento = ehLiderDepartamento && departamentoAtual !== null;
      
      // Logs para debug da lógica de filtragem
      console.log(`[DEBUG] atualizarDados - Valor calculado de tarefasDepartamento=${tarefasDepartamento}`);
      console.log(`[DEBUG] atualizarDados - Departamento atual: ${departamentoAtual ? departamentoAtual.id + ' - ' + departamentoAtual.nome : 'não definido'}`);
      
      // Passar os parâmetros de visualização de departamento e tipo para a API
      // O KanbanContext.tsx aplicará a lógica de filtragem com base nesses parâmetros
      await recarregarTarefas(tarefasDepartamento, tipo);
      console.log('Atualização de dados do Kanban concluída com sucesso');
    } catch (error) {
      console.error('Erro ao recarregar tarefas:', error);
      toast.error('Erro ao atualizar o quadro. Tente novamente.');
    }
  }, [recarregarTarefas, ehLiderDepartamento, tipo, departamentoAtual]);

  // Referência para controlar se já carregamos os dados inicialmente
  const dadosCarregadosRef = useRef(false);
  
  // Removida a função iniciarAtualizacaoAutomatica pois o KanbanContext já cuida do polling
  
  // Efeito para carregar dados iniciais e configurar atualização automática
  useEffect(() => {
    // Apenas executar quando estivermos na rota do kanban
    if (!rotaAtual.includes('/kanban')) {
      // Resetar flag para permitir nova carga quando voltar ao kanban
      console.log('Saindo da rota kanban');
      dadosCarregadosRef.current = false;
      return;
    }
    
    // Carregar dados iniciais apenas uma vez
    if (!dadosCarregadosRef.current) {
      console.log('Carregando dados iniciais do Kanban');
      atualizarDados(false);
      dadosCarregadosRef.current = true;
    }
    
    // Verificar se há tarefaId na URL para abrir modal automaticamente
    const params = new URLSearchParams(location.search);
    const tarefaId = params.get('tarefaId');
    
    console.log('🔍 [KanbanBoard] location.search:', location.search);
    console.log('🔍 [KanbanBoard] tarefaId detectado:', tarefaId);
    
    if (tarefaId) {
      console.log(`✅ [KanbanBoard] Abrindo modal automaticamente para tarefa: ${tarefaId}`);
      // Buscar a tarefa e abrir o modal
      tarefaService.obterTarefa(tarefaId)
        .then(response => {
          console.log('📦 [KanbanBoard] Resposta da API:', response);
          if (response.success && response.tarefa) {
            const tarefaComComentarios = {
              ...response.tarefa,
              comentarios: mapearComentarios(response.tarefa.comentarios || [])
            };
            console.log('✅ [KanbanBoard] Abrindo modal com tarefa:', tarefaComComentarios);
            setTarefaSelecionada(tarefaComComentarios);
            setModalAberto(true);
            
            // Remover tarefaId da URL após abrir o modal
            const url = new URL(window.location.href);
            url.searchParams.delete('tarefaId');
            window.history.replaceState(null, '', url.pathname + url.search + url.hash);
            console.log('✅ [KanbanBoard] tarefaId removido da URL');
          } else {
            console.error('❌ [KanbanBoard] Tarefa não encontrada na resposta');
          }
        })
        .catch(error => {
          console.error('❌ [KanbanBoard] Erro ao carregar tarefa do email:', error);
          toast.error('Erro ao abrir tarefa');
        });
    } else {
      console.log('ℹ️ [KanbanBoard] Nenhum tarefaId na URL');
    }
    
    // Não iniciamos mais a atualização automática aqui, confiamos no polling do KanbanContext
    
    // Não há mais intervalo local para limpar, pois confiamos no polling do KanbanContext
    return () => {
      // Resetamos a flag para permitir nova carga quando voltar ao kanban
      console.log('Componente KanbanBoard desmontado');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotaAtual, location.search]); // Intencionalmente omitindo outras dependências para evitar loop infinito
  
  // Referência para controlar se já atualizamos os dados ao mudar para tarefas enviadas
  // Inicializar como null para evitar atualização no primeiro carregamento
  const tipoAnteriorRef = useRef<string | null>(null);
  
  // Efeito para atualizar as tarefas quando o usuário muda o tipo de visualização
  // Isso é especialmente importante para garantir que as tarefas corretas apareçam imediatamente
  useEffect(() => {
    // Apenas executar quando houver uma mudança de tipo e não for o primeiro carregamento
    if (tipoAnteriorRef.current !== null && tipoAnteriorRef.current !== tipo) {
      console.log(`Mudou para visualização de tarefas ${tipo}, atualizando dados...`);
      // Forçar uma atualização completa quando mudar o tipo de visualização
      atualizarDados(false);
    }
    
    // Atualizar a referência do tipo anterior
    tipoAnteriorRef.current = tipo;
  }, [tipo]); // Executar apenas quando mudar o tipo de visualização
  
  // Referência para controlar o primeiro carregamento do modo de departamento
  const primeiroCarregamentoDeptRef = useRef<boolean>(true);
  
  // Efeito para atualizar as tarefas quando o usuário muda o modo de visualização de departamento
  useEffect(() => {
    // Apenas executar quando o usuário é líder e não é o primeiro carregamento
    if (ehLiderDepartamento && !primeiroCarregamentoDeptRef.current) {
      console.log(`Modo de visualização de departamento alterado para: ${mostrarTarefasDepartamento}`);
      // Forçar uma atualização completa quando mudar o modo de visualização
      atualizarDados(false);
    }
    
    // Marcar que não é mais o primeiro carregamento
    primeiroCarregamentoDeptRef.current = false;
  }, [mostrarTarefasDepartamento, ehLiderDepartamento]); // Executar apenas quando mudar o modo de visualização ou status de líder
  
  // Função para obter o valor numérico da prioridade para ordenação
  const obterValorPrioridade = (prioridade: string): number => {
    const prioridades: Record<string, number> = {
      'Alta': 0,
      'Média': 1,
      'Baixa': 2
    };
    return prioridades[prioridade] || 3;
  };
  
  // Função para obter os status permitidos para cada coluna
  const obterStatusPermitidos = (tituloColuna: string): string[] => {
    switch(tituloColuna) {
      // TAREFAS RECEBIDAS (onde você é responsável)
      case 'Pendente':
        return ['Pendente', 'Solicitado']; // Backend aceita ambos
      case 'A iniciar':
        return ['A iniciar'];
      case 'Em Andamento':
        return ['Em Andamento', 'Em Execução']; // Backend aceita ambos
      case 'Aguardando Aprovação':
        return ['Aguardando Aprovação', 'Aguardando Resposta']; // Backend aceita ambos
      case 'Concluída':
        return ['Concluída', 'Finalizada']; // Backend aceita ambos
      
      // TAREFAS ENVIADAS (onde você é criador)
      case 'Em Execução':
        return ['Em Andamento', 'Em Execução']; // Backend aceita ambos
      case 'Finalizada':
        return ['Finalizada', 'Concluída']; // Backend aceita ambos
      
      default:
        return [tituloColuna];
    }
  };

  // Efeito para filtrar as tarefas com base no tipo (recebidas ou enviadas)
  // e no status de líder do departamento
  useEffect(() => {
    // Evitar processamento se não temos dados
    if (!quadro || !usuario) return;
    
    console.log(`[DEBUG] Filtragem - Iniciando filtragem local de tarefas`);
    console.log(`[DEBUG] Filtragem - Parâmetros: tipo=${tipo}, mostrarTarefasDepartamento=${mostrarTarefasDepartamento}`);
    console.log(`[DEBUG] Filtragem - Usuário é líder: ${ehLiderDepartamento}, Departamento: ${departamentoAtual?.nome || 'N/A'}`);
    
    // Usar as colunas base corretas com base no tipo
    const colunasBase = tipo === 'recebidas' ? colunasRecebidas : colunasEnviadas;
    
    // Extrair todas as tarefas do quadro
    const todasTarefas = quadro.colunas.flatMap(coluna => coluna.tarefas);
    console.log(`[DEBUG] Filtragem - Total de tarefas no quadro: ${todasTarefas.length}`);
    
    // Aplicar filtro inicial com base no tipo de visualização
    let tarefasFiltradas = todasTarefas;
    
    // Filtro simples e direto baseado no tipo de visualização
    if (tipo === 'recebidas') {
      // Para tarefas recebidas, mostrar apenas onde o usuário é responsável
      tarefasFiltradas = tarefasFiltradas.filter(tarefa => 
        tarefa.responsavel && tarefa.responsavel.id === usuario.id
      );
      console.log(`[DEBUG] Filtragem - Aplicando filtro de tarefas RECEBIDAS: ${tarefasFiltradas.length} tarefas após filtro`);
    } else if (tipo === 'enviadas') {
      // Para tarefas enviadas, mostrar apenas onde o usuário é criador
      tarefasFiltradas = tarefasFiltradas.filter(tarefa => 
        tarefa.criador && tarefa.criador.id === usuario.id
      );
      console.log(`[DEBUG] Filtragem - Aplicando filtro de tarefas ENVIADAS: ${tarefasFiltradas.length} tarefas após filtro`);
    }
    
    // IMPORTANTE: Não aplicamos filtro de departamento aqui, pois isso já foi feito no backend
    // quando o parâmetro tarefasDepartamento foi passado para a API.
    // A filtragem local duplicada estava causando o problema de não exibir as tarefas do departamento.
    
    // Apenas registramos informações de debug para verificar as tarefas do departamento
    if (ehLiderDepartamento && mostrarTarefasDepartamento && departamentoAtual) {
      console.log(`[DEBUG] Filtragem - Verificando tarefas do departamento: ${departamentoAtual.id} (${departamentoAtual.nome})`);
      
      // Apenas para debug, contamos quantas tarefas pertencem ao departamento
      const tarefasDoDepartamento = tarefasFiltradas.filter(tarefa => 
        tarefa.departamentoId === departamentoAtual.id
      );
      
      console.log(`[DEBUG] Filtragem - Tarefas do departamento encontradas: ${tarefasDoDepartamento.length} de ${tarefasFiltradas.length}`);
      
      // Logar detalhes das tarefas do departamento para debug
      tarefasDoDepartamento.forEach(tarefa => {
        console.log(`[DEBUG] Tarefa do departamento: ID=${tarefa.id}, Título=${tarefa.titulo}, DepartamentoId=${tarefa.departamentoId}, Status=${tarefa.status}`);
      });
    } else {
      console.log(`[DEBUG] Filtragem - Não verificando tarefas do departamento. Condições: ehLiderDepartamento=${ehLiderDepartamento}, mostrarTarefasDepartamento=${mostrarTarefasDepartamento}, departamentoAtual=${departamentoAtual ? 'presente' : 'ausente'}`);
    }
    
    // Distribuir as tarefas filtradas nas colunas corretas
    const colunasAtualizadas = colunasBase.map(coluna => ({
      ...coluna,
      tarefas: tarefasFiltradas.filter(tarefa => {
        // Determinar quais status devem ser incluídos em cada coluna
        const statusPermitidos = obterStatusPermitidos(coluna.titulo);
        return statusPermitidos.includes(tarefa.status);
      }).sort((a, b) => {
        // Ordenar por data de criação (mais recentes primeiro)
        const dataCriacaoA = a.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;
        const dataCriacaoB = b.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;
        
        // Ordem decrescente: mais recentes primeiro
        return dataCriacaoB - dataCriacaoA;
      })
    }));
    
    setQuadroFiltrado({
      ...quadro,
      colunas: colunasAtualizadas
    });
    
    console.log(`[DEBUG] Filtragem - Quadro local atualizado com ${colunasAtualizadas.reduce((total, col) => total + col.tarefas.length, 0)} tarefas no total`);
  }, [quadro, tipo, usuario, ehLiderDepartamento, usuariosDepartamento, mostrarTarefasDepartamento, departamentoAtual, ehAdmin]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    console.log('Drag end event:', result);
    
    // Verificar se o item foi solto fora de uma área válida
    if (!destination) {
      console.log('Destino inválido, cancelando operação');
      return;
    }
    
    // Verificar se o item foi solto na mesma posição
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('Origem e destino são iguais, nenhuma ação necessária');
      return;
    }
    
    // Encontrar a tarefa que está sendo movida
    const tarefaId = result.draggableId;
    
    // Encontrar as colunas de origem e destino no quadro filtrado
    const colunaOrigem = quadroFiltrado.colunas.find(col => col.id === source.droppableId);
    const colunaDestino = quadroFiltrado.colunas.find(col => col.id === destination.droppableId);
    
    if (!colunaOrigem || !colunaDestino) {
      console.error('Coluna de origem ou destino não encontrada');
      return;
    }
    
    console.log(`Movendo tarefa ${tarefaId} de '${colunaOrigem.titulo}' para '${colunaDestino.titulo}'`);
    
    // Chamar a função moverTarefa com os IDs corretos
    moverTarefa(tarefaId, source.droppableId, destination.droppableId);
    
    // O quadroFiltrado será atualizado automaticamente pelo useEffect quando o quadro original mudar
  }, [moverTarefa, quadroFiltrado]);

  const abrirDetalhes = async (tarefa: Tarefa) => {
    if (!tarefa || !tarefa.id) {
      console.error('Tentativa de abrir detalhes de tarefa inválida');
      return;
    }
    
    try {
      // Buscar dados atualizados da tarefa da API
      const tarefaAtualizada = await tarefaService.obterTarefa(tarefa.id);
      
      // Verificar se a tarefa atualizada é válida e tem as propriedades necessárias
      if (tarefaAtualizada && tarefaAtualizada.id) {
        // Garantir que a tarefa tenha todas as propriedades necessárias
        const tarefaCompleta = {
          ...tarefa, // Manter os dados originais como fallback
          ...tarefaAtualizada, // Sobrescrever com os dados atualizados
          // Garantir que propriedades críticas existam
          responsavel: tarefaAtualizada.responsavel || tarefa.responsavel || { id: '', nome: 'Não atribuído' },
          criador: tarefaAtualizada.criador || tarefa.criador || { id: '', nome: 'Desconhecido' },
          // Mapear os comentários usando o utilitário de mapeamento
          comentarios: mapearComentarios(tarefaAtualizada.comentarios || [])
        };
        
        setTarefaSelecionada(tarefaCompleta);
        setModalAberto(true);
      } else {
        // Fallback para os dados existentes caso a API falhe
        // Garantir que a tarefa original tenha as propriedades necessárias
        const tarefaSegura = {
          ...tarefa,
          responsavel: tarefa.responsavel || { id: '', nome: 'Não atribuído' },
          criador: tarefa.criador || { id: '', nome: 'Desconhecido' },
          comentarios: tarefa.comentarios || []
        };
        
        setTarefaSelecionada(tarefaSegura);
        setModalAberto(true);
        console.warn('Não foi possível obter dados atualizados da tarefa, usando dados em cache.');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da tarefa:', error);
      
      // Fallback para os dados existentes caso a API falhe
      // Garantir que a tarefa original tenha as propriedades necessárias
      const tarefaSegura = {
        ...tarefa,
        responsavel: tarefa.responsavel || { id: '', nome: 'Não atribuído' },
        criador: tarefa.criador || { id: '', nome: 'Desconhecido' },
        comentarios: tarefa.comentarios || []
      };
      
      setTarefaSelecionada(tarefaSegura);
      setModalAberto(true);
      toast.error('Erro ao carregar detalhes atualizados da tarefa.');
    }
  };
  
  const abrirModalNovaTarefa = (colunaId?: string) => {
    if (colunaId) {
      const coluna = quadro.colunas.find(col => col.id === colunaId);
      if (coluna) {
        setColunaInicialNovaTarefa(coluna.titulo);
      }
    } else {
      setColunaInicialNovaTarefa('Solicitado'); // Coluna padrão
    }
    setNovaTarefaModalAberto(true);
  };

  // Manipulador para iniciar o arrasto para scroll
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Obter o elemento alvo do evento
    const target = e.target as HTMLElement;
    
    // Verificar se o clique foi em uma área que deve permitir arrasto
    // Permitir arrasto se for em qualquer área vazia do quadro
    const isClickOnDraggableArea = (
      // Clique direto no container principal
      e.target === e.currentTarget ||
      target.classList.contains('kanban-board-container') ||
      
      // Áreas vazias dentro das colunas
      target.classList.contains('droppable-area') ||
      
      // Espaços entre colunas ou áreas vazias
      target.classList.contains('p-3') ||
      target.classList.contains('flex-grow') ||
      
      // Áreas vazias com mensagem "Arraste tarefas aqui"
      target.classList.contains('flex-col') && target.classList.contains('items-center') ||
      
      // Verificar se o pai é uma área arrástavel
      (target.parentElement && 
        (target.parentElement.classList.contains('droppable-area') ||
         target.parentElement.classList.contains('kanban-board-container')))
    );
    
    // Não permitir arrasto em elementos interativos ou que precisam de clique
    const isClickOnInteractiveElement = (
      // Cards e elementos interativos
      target.closest('.kanban-card') ||
      target.closest('button') ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.tagName === 'A' ||
      
      // Elementos de drag and drop
      target.hasAttribute('draggable') ||
      target.closest('[draggable="true"]') ||
      
      // Verificar se tem algum manipulador de clique
      target.onclick ||
      target.getAttribute('onClick')
    );
    
    // Iniciar o arrasto se for em uma área válida e não for um elemento interativo
    if ((isClickOnDraggableArea || !isClickOnInteractiveElement) && !target.closest('.kanban-card')) {
      setIsDraggingScroll(true);
      setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0));
      setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
      
      // Mudar o cursor para indicar que o arrasto está ativo
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grabbing';
      }
      
      // Prevenir comportamento padrão para evitar conflitos
      e.preventDefault();
    }
  };

  // Manipulador para o movimento durante o arrasto
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingScroll) return;
    
    // Prevenir seleção de texto durante o arrasto
    e.preventDefault();
    
    // Calcular a nova posição de scroll
    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1.5; // Multiplicador para ajustar a velocidade do scroll
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  // Manipulador para finalizar o arrasto
  const handleMouseUp = () => {
    setIsDraggingScroll(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  // Manipulador para quando o mouse sai da área
  const handleMouseLeave = () => {
    if (isDraggingScroll) {
      setIsDraggingScroll(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
    }
  };

  // Adicionar e remover event listeners
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.style.cursor = 'grab';
    }

    // Limpar event listeners quando o componente for desmontado
    return () => {
      if (scrollContainer) {
        scrollContainer.style.cursor = '';
      }
    };
  }, []);

  const fecharDetalhes = () => {
    setModalAberto(false);
    setTarefaSelecionada(null);
  };

  // Função para duplicar tarefa
  const handleDuplicarTarefa = (tarefa: Tarefa) => {
    setTarefaParaDuplicar(tarefa);
    setNovaTarefaModalAberto(true);
    toast.info('Preencha os dados da nova tarefa baseada na duplicada');
  };

  // Função para determinar a cor do cabeçalho da coluna
  const getColumnHeaderColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'solicitado':
        return 'bg-bravo-dark';
      case 'validação':
        return 'bg-gray-700';
      case 'planejamento':
        return 'bg-gray-600';
      case 'a iniciar':
        return 'bg-teal-500'; // Cor para a nova coluna "A iniciar"
      case 'em execução':
        return 'bg-bravo-secondary';
      case 'aguardando aprovação':
        return 'bg-gray-500';
      case 'em ajustes':
        return 'bg-gray-600';
      case 'finalizado':
        return 'bg-bravo-primary';
      case 'cancelado':
        return 'bg-gray-400';
      case 'pausado':
        return 'bg-gray-500';
      default:
        return 'bg-bravo-secondary';
    }
  };

  // Função para alternar entre visualização pessoal e departamento removida
  // Agora líderes sempre veem tarefas do departamento e usuários comuns sempre veem suas próprias tarefas

  // Efeito para atualizar o título da página
  useEffect(() => {
    document.title = `BravoHub - ${tipo === 'recebidas' ? 'Tarefas Recebidas' : 'Tarefas Enviadas'}${mostrarTarefasDepartamento ? ' (Departamento)' : ''}`;
  }, [tipo, mostrarTarefasDepartamento]);

  // Efeito para adicionar classes aos elementos do Kanban removido

  // Efeito relacionado ao tutorial removido

  return (
    <div className="h-full flex flex-col">
      <div className="kanban-header flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-bravo-dark dark:text-gray-100">
            {tipo === 'recebidas' ? (
              mostrarTarefasDepartamento ? 
                `Tarefas do Departamento ${departamentoAtual?.nome || ''}` : 
                'Tarefas Recebidas'
            ) : (
              mostrarTarefasDepartamento ? 
                `Tarefas Enviadas pelo Departamento ${departamentoAtual?.nome || ''}` : 
                'Tarefas Enviadas'
            )}
        </h2>
        <div className="flex space-x-2">
          {/* Botão de alternar visualização removido conforme solicitado */}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => abrirModalNovaTarefa()}
            className="bg-bravo-primary hover:bg-bravo-primary-dark nova-tarefa-btn"
          >
            Nova Tarefa
          </Button>
          {/* Botão de tutorial removido */}
        </div>
      </div>
      
      <div className="flex items-center mb-4">
        {tipo === 'enviadas' && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => abrirModalNovaTarefa()}
            className="mr-4"
          >
            Nova Tarefa
          </Button>
        )}
        
        <Button
          variant="outlined"
          startIcon={<ArchiveIcon />}
          onClick={() => navigate('/tarefas-arquivadas')}
        >
          Arquivadas
        </Button>
      </div>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <div 
          ref={scrollContainerRef}
          className={`flex overflow-x-auto pb-6 h-full kanban-board-container ${isDraggingScroll ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ 
            minHeight: 'calc(100vh - 220px)',
            scrollBehavior: 'smooth',
            userSelect: 'none', // Previne seleção de texto durante o arrasto
            gap: '16px', // Espaço entre as colunas para facilitar o arrasto
            padding: '8px' // Padding adicional para facilitar o arrasto nas bordas
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {quadroFiltrado.colunas.map((coluna) => (
            <div 
              key={coluna.id} 
              className="flex-shrink-0 w-80 mx-2 first:ml-0 last:mr-0"
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-card h-full flex flex-col border border-gray-200 dark:border-gray-700">
                <div
                  className={`p-3 font-medium text-white rounded-t-lg flex justify-between items-center ${getColumnHeaderColor(coluna.titulo)}`}
                >
                  <h3 className="font-semibold">{coluna.titulo}</h3>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white text-bravo-dark text-xs font-semibold px-2 py-1 rounded-full">
                      {coluna.tarefas.length}
                    </span>
                  </div>
                </div>

                <Droppable droppableId={coluna.id} type="TAREFA">
                  {(provided, snapshot) => {
                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-3 flex-grow overflow-y-auto droppable-area ${snapshot.isDraggingOver ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}
                        style={{ minHeight: '200px', maxHeight: 'calc(100vh - 220px)' }}
                      >
                        {coluna.tarefas.length === 0 && (
                          <div className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Arraste tarefas aqui
                          </div>
                        )}
                        {coluna.tarefas.map((tarefa, index) => (
                          <Draggable 
                            key={tarefa.id} 
                            draggableId={tarefa.id} 
                            index={index}
                          >
                            {(provided, snapshot) => {
                              return (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => abrirDetalhes(tarefa)}
                                  className={`mb-3 last:mb-0 kanban-card ${snapshot.isDragging ? 'shadow-lg' : ''} transition-shadow`}
                                  style={{
                                    ...provided.draggableProps.style,
                                    opacity: snapshot.isDragging ? 0.8 : 1
                                  }}
                                >
                                  <KanbanCard 
                                    tarefa={tarefa} 
                                    usuarioLogadoId={usuario?.id || ''}
                                    mostrarTarefasDepartamento={ehLiderDepartamento && mostrarTarefasDepartamento}
                                    tipo={tipo}
                                  />
                                </div>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    );
                  }}
                </Droppable>
              </div>
            </div>
          ))}
        </div>
      </DragDropContext>
      
      {tarefaSelecionada && (
        <TaskDetailsModal 
          tarefa={tarefaSelecionada as Tarefa} 
          aberto={modalAberto} 
          onFechar={fecharDetalhes}
          onDuplicar={handleDuplicarTarefa}
        />
      )}

      <NovaTarefaModal
        isOpen={novaTarefaModalAberto}
        onClose={() => {
          setNovaTarefaModalAberto(false);
          setTarefaParaDuplicar(null);
        }}
        colunaInicial={colunaInicialNovaTarefa}
        tarefaParaDuplicar={tarefaParaDuplicar}
      />
    </div>
  );
};

export default KanbanBoard;
